import { buildAuditEvidenceContract } from './auditEvidenceContract.js';

// The contract builder consumes the persisted snapshot shape. Project only
// observed client-run fields into that shape; never substitute a field p75 for
// an absent laboratory run or turn an aggregate into individual run values.
export function buildPanelEvidenceContract(payload) {
  // A browser-supplied projection is not an independently verified raw snapshot.
  // Match the server's untrusted persistence boundary before rendering or saving.
  const clientReported = contract => ({
    ...contract,
    records: contract.records.map(record => ({
      ...record,
      provenance: { ...record.provenance, kind: 'client_reported', rawVerified: false },
      causalEvidence: {
        ...record.causalEvidence,
        ...(record.causalEvidence?.state !== 'unknown'
          ? { state: 'unconfirmed', reason: 'client_evidence_not_verified' } : {}),
        associations: (record.causalEvidence?.associations || []).map(association => ({
          ...association, level: 'unconfirmed'
        }))
      }
    })),
    fieldRecords: contract.fieldRecords.map(record => ({
      ...record, provenance: { kind: 'client_reported', rawVerified: false }
    })),
    crossPage: { ...contract.crossPage, provenance: 'client_reported', rawVerified: false,
      sameMechanismAttributed: false, templateAttributed: false,
      sameMechanismAttributedPages: 0, templateAttributedPages: 0 },
    crossPageByMetric: Object.fromEntries(
      Object.entries(contract.crossPageByMetric || {}).map(([metric, summary]) => [
        metric, { ...summary, provenance: 'client_reported', rawVerified: false,
          sameMechanismAttributed: false, templateAttributed: false,
          sameMechanismAttributedPages: 0, templateAttributedPages: 0 }
      ])
    )
  });
  if (payload.lab_data || payload.labData) return clientReported(buildAuditEvidenceContract(payload));
  const deep = {
    INP: payload.deepINPResults,
    CLS: payload.deepCLSResults,
    LCP: payload.deepLCPResults,
    TTFB: payload.deepTTFBResults
  };
  const metric = String(payload.metric || '').toUpperCase();
  const metrics = {};
  for (const [name, result] of Object.entries(deep)) {
    if (!result) continue;
    const value = result[name.toLowerCase()];
    const valid = typeof value === 'number' && Number.isFinite(value)
      && (name === 'CLS' || value > 0)
      && result.success !== false && result.noValidInteraction !== true
      && result.attributionValid !== false;
    const spread = result.labRunSpread;
    metrics[name] = {
      url: result.url || payload.url,
      device: result.device ?? null,
      valid, value: valid ? value : null,
      diagnosticEvidence: result.diagnosticEvidence,
      scenarios: result.interactionDiagnostics?.scenarios,
      conditions: spread?.conditions,
      ...(spread ? { repeat: {
        validRuns: spread.validRuns,
        values: Array.isArray(spread.values) ? spread.values : [],
        attempts: Array.isArray(spread.attempts) ? spread.attempts : [],
        validRunConditionsConsistent: spread.validRunConditionsConsistent,
        conditions: spread.conditions
      } } : {})
    };
  }
  const sweeps = {};
  for (const [name, results] of Object.entries({
    INP: payload.multiPageINPResults,
    CLS: payload.multiPageResults,
    LCP: payload.multiPageLCPResults,
    TTFB: payload.multiPageTTFBResults
  })) {
    const pages = results?.pages || results?.pageResults;
    if (!Array.isArray(pages)) continue;
    sweeps[name] = { pages: pages.map(page => ({
      url: page.url, device: page.device ?? null, value: page[name.toLowerCase()],
      validRuns: page.validRuns, measurementValid: page.measurementValid ?? page.valid,
      diagnosticEvidence: page.diagnosticEvidence,
      repeat: page.repeat
    })) };
  }
  const fieldRecords = typeof payload.fieldValue === 'number' && Number.isFinite(payload.fieldValue)
    && ['INP', 'CLS', 'LCP', 'TTFB'].includes(metric)
    ? [{ metric, device: payload.fieldDevice ?? null, p75: payload.fieldValue,
      scope: 'field-p75', url: payload.url }] : [];
  const tbtValue = payload.diagnostics?.longTasks?.totalBlockingTime;
  const tbt = typeof tbtValue === 'number' && Number.isFinite(tbtValue)
    ? { value: tbtValue, source: 'lighthouse', url: payload.url } : null;
  return clientReported(buildAuditEvidenceContract({
    url: payload.url,
    fieldData: { records: fieldRecords },
    labData: { metrics, sweeps, ...(tbt ? { loading: { tbt } } : {}) }
  }));
}